// ── API — ogni funzione che parla con Supabase (database, storage, auth) ────
import { supa } from './supabase.js';
import { MP } from './constants.js';
import { shapeMsg } from './helpers.js';
import { TODAY, YR } from './dateState.js';
export async function uploadPhotoToStorage(blob,userId){
  const path=`${userId}/${Date.now()}.jpg`;
  const {error}=await supa.storage.from("photos").upload(path,blob,{contentType:"image/jpeg"});
  if(error) throw error;
  const {data}=supa.storage.from("photos").getPublicUrl(path);
  return data.publicUrl;
}
export async function uploadAvatarToStorage(blob,userId){
  const path=`${userId}/avatar-${Date.now()}.jpg`;
  const {error}=await supa.storage.from("photos").upload(path,blob,{contentType:"image/jpeg",upsert:true});
  if(error) throw error;
  const {data}=supa.storage.from("photos").getPublicUrl(path);
  return data.publicUrl;
}
export async function uploadCoverToStorage(blob,userId){
  const path=`${userId}/cover-${Date.now()}.jpg`;
  const {error}=await supa.storage.from("photos").upload(path,blob,{contentType:"image/jpeg",upsert:true});
  if(error) throw error;
  const {data}=supa.storage.from("photos").getPublicUrl(path);
  return data.publicUrl;
}
export async function updateProfileAvatar(userId,avatarUrl){
  const {error}=await supa.from("profiles").update({avatar_url:avatarUrl}).eq("id",userId);
  if(error){ console.error("Oncer: salvataggio foto profilo fallito —",error.message); return false; }
  return true;
}
export async function updateProfileCover(userId,coverUrl){
  const {error}=await supa.from("profiles").update({cover_url:coverUrl}).eq("id",userId);
  if(error){ console.error("Oncer: salvataggio copertina fallito —",error.message); return false; }
  return true;
}
export async function updateProfileBio(userId,bio){
  const {error}=await supa.from("profiles").update({bio}).eq("id",userId);
  if(error){ console.error("Oncer: salvataggio bio fallito —",error.message); return false; }
  return true;
}
export async function updateProfileTheme(userId,theme){
  const {error}=await supa.from("profiles").update({theme}).eq("id",userId);
  if(error){ console.error("Oncer: salvataggio tema fallito —",error.message); return false; }
  return true;
}
export async function fetchRewards(userId){
  const {data,error}=await supa.from("profiles").select("rewards").eq("id",userId).single();
  if(error||!data||!data.rewards){ return {unlocked:[],claimed:[],equippedDecorations:[],decorationPositions:{},dailyStreak:{count:0,lastDay:null,lastYear:null}}; }
  // Importante: si parte da uno spread di TUTTO quello che c'è davvero nel
  // database (...data.rewards), non da una lista fissa di campi scelti a
  // mano — altrimenti ogni campo nuovo aggiunto in futuro (come
  // decorationPositions, le posizioni delle coccarde sullo scaffale) rischia
  // di essere silenziosamente perso ad ogni ricaricamento dell'app, e poi
  // sovrascritto per davvero sul database dal salvataggio automatico in
  // App.jsx. I campi sotto hanno solo un valore di riserva se mancanti.
  return {
    ...data.rewards,
    unlocked:data.rewards.unlocked||[],
    claimed:data.rewards.claimed||[],
    equippedDecorations:data.rewards.equippedDecorations||[],
    decorationPositions:data.rewards.decorationPositions||{},
    dailyStreak:data.rewards.dailyStreak||{count:0,lastDay:null,lastYear:null},
  };
}
export async function updateRewards(userId,rewards){
  const {error}=await supa.from("profiles").update({rewards}).eq("id",userId);
  if(error){ console.error("Oncer: salvataggio ricompense fallito —",error.message); return false; }
  return true;
}
// Esportazione completa e sempre aggiornata — a differenza della versione
// precedente (che usava solo quello già caricato in memoria durante la
// sessione), questa richiede tutto di nuovo al database nel momento esatto
// del download: pagine di ogni anno, micro-libri, filtri creati, ricompense,
// conversazioni reali, messaggi di Oncer Future inviati e ricevuti.
export async function fetchFullUserExport(userId){
  const [profileRes,pagesByYear,micro,rewards,threads,sentFuture,deliveredFuture,filtersRes]=await Promise.all([
    supa.from("profiles").select("*").eq("id",userId).single(),
    fetchRemoteBooks(userId),
    fetchMyMicroBooks(userId),
    fetchRewards(userId),
    fetchMyThreads(userId),
    fetchMySentFutureMessages(userId),
    fetchDeliveredFutureMessages(userId),
    supa.from("filters").select("*").eq("creator_id",userId),
  ]);
  // Per ogni conversazione reale, prendo anche tutti i messaggi (fetchMyThreads
  // da sola porta solo l'ultimo, per la lista — qui invece serve tutta la storia).
  const fullThreads=await Promise.all(threads.map(async th=>{
    const msgs=await fetchThreadMessages(th.realThreadId);
    return {with:th.user,messages:msgs.map(m=>({from:m.sender_id===userId?"tu":th.user,text:m.text,sentAt:m.created_at}))};
  }));
  return {
    exportedAt:new Date().toISOString(),
    profile:profileRes.data||null,
    pagesByYear,
    microBooks:micro,
    filtersCreated:filtersRes.data||[],
    rewards,
    conversations:fullThreads,
    futureMessagesSent:sentFuture,
    futureMessagesUnlocked:deliveredFuture,
  };
}
export async function fetchMemoryForToday(userId){
  const lastYear=YR-1;
  const sameCalendarDate=new Date(lastYear,TODAY.getMonth(),TODAY.getDate());
  const lastYearDOY=Math.ceil((sameCalendarDate-new Date(lastYear,0,1))/86400000);
  const {data,error}=await supa.from("pages").select("*").eq("user_id",userId).eq("year",lastYear).eq("day",lastYearDOY).maybeSingle();
  if(error){ console.error("Oncer: caricamento ricordo fallito —",error.message); return null; }
  if(!data) return null;
  return {photo:data.photo,caption:data.caption,day:data.day,year:data.year,filter:data.filter_id||"none"};
}
// Genera un codice breve, leggibile, adatto a comparire in un link/QR
// pubblico — es. "k3f9zq2p". Non serve robustezza crittografica: se per
// assurdo collidesse con un codice già esistente, l'insert fallirebbe per
// il vincolo "unique" sulla colonna e basterebbe riprovare (la probabilità
// di collisione su questo spazio di caratteri è comunque trascurabile per
// i volumi che questa funzione avrà).
function generateInviteCode(){
  return Math.random().toString(36).slice(2,10);
}
export async function createSharedBook(userId,{title,preset,theme,kind,photoLimit}){
  const insertData={title,preset,theme,kind,created_by:userId};
  // Solo i libri EVENTO hanno un codice invito pubblico — è quello che va
  // dentro il QR/link lasciato agli invitati per unirsi senza account.
  if(kind==="evento"){
    insertData.invite_code=generateInviteCode();
    // Limite foto scelto da chi crea l'evento (50/100/150/200) — null vuol
    // dire "nessun limite" (costo calcolato a evento concluso). Aggiunto il
    // 31/08/2026 insieme allo step di scelta del limite nel pannello di
    // creazione: prima questo dato non veniva salvato da nessuna parte.
    insertData.photo_limit=(typeof photoLimit==="number"&&photoLimit>0)?photoLimit:null;
  }
  const {data,error}=await supa.from("shared_books").insert(insertData).select().single();
  if(error){ console.error("Oncer: creazione libro condiviso fallita —",error.message); return null; }
  const {error:memErr}=await supa.from("shared_book_members").insert({shared_book_id:data.id,user_id:userId,status:"accepted"});
  if(memErr) console.error("Oncer: aggiunta membro (creatore) fallita —",memErr.message);
  return data;
}
// Chiude un libro EVENTO (solo chi lo ha creato può farlo — controllo vero
// fatto lato server nella Edge Function, questo è solo il wrapper client).
// Alla chiusura, la funzione manda anche la mail con l'anteprima a tutti i
// partecipanti — non serve fare altro da qui.
export async function closeEventBook(sharedBookId){
  const {data,error}=await supa.functions.invoke("close-event-book",{body:{sharedBookId}});
  if(error){ console.error("Oncer: chiusura libro evento fallita —",error.message); return {ok:false}; }
  return data||{ok:true};
}
// Genera il PDF pronto per la stampa del libro (bordo di abbondanza + area di
// sicurezza) tramite la Edge Function generate-print-pdf, e restituisce l'URL
// firmato temporaneo (valido 1h) da cui scaricarlo. Uso attuale: solo il test
// manuale da admin in Print.jsx, in attesa dell'integrazione col partner di
// stampa vero (Gelato o altro) — vedi commento in cima alla Edge Function.
export async function generatePrintPdf({userId,bookTitle,pages,totalDaysInYear,format,includeEmptyPages,themeColor,layout,minPageCount}){
  const {data,error}=await supa.functions.invoke("generate-print-pdf",{body:{userId,bookTitle,pages,totalDaysInYear,format,includeEmptyPages,themeColor,layout,minPageCount}});
  if(error){ console.error("Oncer: generazione PDF di stampa fallita —",error.message); return {ok:false,error:error.message}; }
  if(data&&data.error){ console.error("Oncer: generazione PDF di stampa fallita —",data.error); return {ok:false,error:data.error}; }
  return data||{ok:false,error:"Risposta vuota"};
}
// Preventivo REALE di stampa: interroga dal vivo Gelato e/o Prodigi (in
// base allo stile scelto — vedi vendorsForStyle in helpers.js) tramite la
// Edge Function get-print-quote, che sceglie in automatico il fornitore
// migliore tra quelli compatibili e restituisce un prezzo vero, non stimato.
// Se le chiavi API dei fornitori non sono ancora configurate lato server,
// torna {ok:true,fallback:true,...} con la stima interna come ripiego,
// così il wizard può comunque mostrare un numero invece di bloccarsi.
export async function fetchPrintQuote({format,copies,shippingCountry,forceVendor,debugSpinePageCounts}){
  const {data,error}=await supa.functions.invoke("get-print-quote",{body:{format,copies,shippingCountry,forceVendor,debugSpinePageCounts}});
  if(error){ console.error("Oncer: preventivo di stampa fallito —",error.message); return {ok:false,error:error.message}; }
  if(data&&data.error){ console.error("Oncer: preventivo di stampa fallito —",data.error); return {ok:false,error:data.error}; }
  return data||{ok:false,error:"Risposta vuota"};
}
// Crea davvero l'ordine: salva la riga in print_orders (stato
// "awaiting_payment") e apre una sessione di pagamento Stripe Checkout.
// L'ordine NON viene mandato al fornitore di stampa qui — questo avviene
// solo dopo il pagamento confermato, dentro il webhook Stripe
// (stripe-print-webhook), per non rischiare mai di spedire un ordine a
// pagamento non riuscito. Se Stripe non è ancora configurato lato server
// (nessuna chiave), torna {ok:false,notConfigured:true} così la UI può
// mostrare un messaggio chiaro invece di un errore generico.
export async function createPrintCheckout({userId,book,format,copies,pdfUrl,shipping,quote,forceVendor}){
  const {data,error}=await supa.functions.invoke("create-print-checkout",{body:{userId,book,format,copies,pdfUrl,shipping,quote,forceVendor}});
  if(error){ console.error("Oncer: avvio pagamento stampa fallito —",error.message); return {ok:false,error:error.message}; }
  if(data&&data.error){ console.error("Oncer: avvio pagamento stampa fallito —",data.error); return {ok:false,error:data.error,notConfigured:data.notConfigured||false}; }
  return data||{ok:false,error:"Risposta vuota"};
}
export async function addSharedBookMember(sharedBookId,userId,fromUserId,bookInfo){
  const {error}=await supa.from("shared_book_members").insert({shared_book_id:sharedBookId,user_id:userId,status:"pending"});
  if(error){ console.error("Oncer: invito membro fallito —",error.message); return false; }
  const {error:notifErr}=await supa.from("notifications").insert({user_id:userId,type:"shared_book_invite",from_user_id:fromUserId,data:{sharedBookId,...bookInfo}});
  if(notifErr) console.error("Oncer: notifica invito libro condiviso non creata —",notifErr.message);
  return true;
}
export async function fetchPendingInvites(userId){
  const {data,error}=await supa.from("shared_book_members").select("shared_book_id,shared_books(id,title,preset,theme,kind,created_by)").eq("user_id",userId).eq("status","pending");
  if(error){ console.error("Oncer: caricamento inviti fallito —",error.message); return []; }
  const creatorIds=[...new Set((data||[]).map(r=>r.shared_books&&r.shared_books.created_by).filter(Boolean))];
  const {data:creators}=creatorIds.length?await supa.from("profiles").select("id,username").in("id",creatorIds):{data:[]};
  const nameById={}; (creators||[]).forEach(p=>{ nameById[p.id]=p.username; });
  return (data||[]).filter(r=>r.shared_books).map(r=>({
    sharedBookId:r.shared_book_id,title:r.shared_books.title,preset:r.shared_books.preset,
    theme:r.shared_books.theme,kind:r.shared_books.kind,
    icon:(MP.find(p=>p.id===r.shared_books.preset)||MP[0]).icon,
    from:nameById[r.shared_books.created_by]||"qualcuno",
  }));
}
// notifId: id della notifica "shared_book_invite" a cui si sta rispondendo.
// Va cancellata dal database qui, non solo dallo stato locale — altrimenti
// riapparirebbe ogni volta che il pannello notifiche viene ricaricato dal
// server, anche dopo averla già accettata o rifiutata.
export async function respondToInvite(sharedBookId,userId,accept,notifId){
  if(accept){
    const {error}=await supa.from("shared_book_members").update({status:"accepted"}).eq("shared_book_id",sharedBookId).eq("user_id",userId);
    if(error){ console.error("Oncer: accettazione invito fallita —",error.message); return false; }
  } else {
    const {error}=await supa.from("shared_book_members").delete().eq("shared_book_id",sharedBookId).eq("user_id",userId);
    if(error){ console.error("Oncer: rifiuto invito fallito —",error.message); return false; }
  }
  if(notifId){
    const {error:delErr}=await supa.from("notifications").delete().eq("id",notifId);
    if(delErr) console.error("Oncer: rimozione notifica invito fallita —",delErr.message);
  }
  return true;
}
export async function fetchMySharedBooks(userId){
  const {data:memberships,error}=await supa.from("shared_book_members").select("shared_book_id").eq("user_id",userId).eq("status","accepted");
  if(error){ console.error("Oncer: caricamento libri condivisi fallito —",error.message); return []; }
  if(!memberships||memberships.length===0) return [];
  const ids=memberships.map(m=>m.shared_book_id);
  const [{data:books},{data:allPages},{data:allMembers}]=await Promise.all([
    supa.from("shared_books").select("*").in("id",ids),
    supa.from("shared_pages").select("shared_book_id").in("shared_book_id",ids),
    supa.from("shared_book_members").select("shared_book_id,user_id,profiles(username)").in("shared_book_id",ids),
  ]);
  const pageCounts={}; (allPages||[]).forEach(p=>{ pageCounts[p.shared_book_id]=(pageCounts[p.shared_book_id]||0)+1; });
  const membersByBook={}; (allMembers||[]).forEach(m=>{ (membersByBook[m.shared_book_id]=membersByBook[m.shared_book_id]||[]).push((m.profiles&&m.profiles.username)||"?"); });
  return (books||[]).map(b=>({
    id:b.id,real:true,shared:true,kind:b.kind,preset:b.preset,
    icon:(MP.find(p=>p.id===b.preset)||MP[0]).icon,cover:(MP.find(p=>p.id===b.preset)||MP[0]).icon,
    title:b.title,pages:pageCounts[b.id]||0,
    // "family" non ha mai un tetto; "evento" usa il limite scelto da chi lo
    // ha creato (photo_limit, null = nessun limite → Infinity); gli altri
    // condivisi ("micro"/a tempo) restano fissi a 20 come prima.
    max:b.kind==="family"?9999:b.kind==="evento"?(b.photo_limit||Infinity):20,
    photoLimit:b.kind==="evento"?(b.photo_limit||null):null,
    closed:b.closed,theme:b.theme,
    members:membersByBook[b.id]||[],
    // created_by e invite_code servono solo ai libri EVENTO — per capire se
    // chi guarda è il creatore (quindi può vedere il QR e chiudere il
    // libro) e per ricostruire il link pubblico da condividere.
    created_by:b.created_by,invite_code:b.invite_code||null,
    dates:new Date(b.created_at).toLocaleDateString("it-IT",{day:"numeric",month:"short",year:"numeric"}),
  }));
}
export async function fetchSharedBookPages(sharedBookId){
  const {data,error}=await supa.from("shared_pages").select("*, profiles(username,theme)").eq("shared_book_id",sharedBookId).order("created_at",{ascending:true});
  if(error){ console.error("Oncer: caricamento pagine libro condiviso fallito —",error.message); return {pages:[],error:error.message}; }
  return {pages:data||[],error:null};
}
export async function postToSharedBook(sharedBookId,userId,{photo,caption,day,year}){
  const {error}=await supa.from("shared_pages").insert({shared_book_id:sharedBookId,user_id:userId,photo,caption:caption||null,day,year});
  if(error){
    console.error("Oncer: pubblicazione nel libro condiviso fallita —",error.message);
    // I libri condivisi "family"/"a tempo" (non gli EVENTO) sono limitati a
    // una sola foto al giorno PER UTENTE — ogni membro può pubblicare la
    // propria, indipendentemente da quante ne abbiano già pubblicate gli
    // altri. Un trigger nel database (enforce_one_shared_page_per_day, vedi
    // correggi_limite_foto_giorno_per_utente.sql) rifiuta l'inserimento se
    // SEI TU ad aver già pubblicato oggi su questo libro, riusando lo stesso
    // codice d'errore 23505 di un vincolo unico. Lo distinguiamo qui per
    // poter mostrare un messaggio che spiega davvero cos'è successo, invece
    // di un generico "non ci sono riuscito" che sembra un errore tecnico.
    const alreadyToday=error.code==="23505"&&error.message&&error.message.includes("shared_pages_shared_book_id_year_day_key");
    return alreadyToday?"already_today":false;
  }
  return true;
}
// Quali, tra questi libri condivisi, ho GIÀ una mia foto per il giorno
// indicato — usata in Capture.jsx per segnalare PRIMA del tentativo (non
// solo dopo un fallimento) che ho già usato oggi un libro "family"/"a
// tempo", visto che per quei libri il limite è una foto al giorno PER
// UTENTE (i libri EVENTO invece non hanno questo limite, quindi non serve
// controllarli). Filtrato per userId: la foto pubblicata oggi da un altro
// membro non deve bloccare me.
export async function fetchSharedBooksPostedToday(sharedBookIds,userId,day,year){
  if(!sharedBookIds||sharedBookIds.length===0) return new Set();
  const {data,error}=await supa.from("shared_pages").select("shared_book_id").in("shared_book_id",sharedBookIds).eq("user_id",userId).eq("day",day).eq("year",year);
  if(error){ console.error("Oncer: controllo foto già pubblicata oggi (libri condivisi) fallito —",error.message); return new Set(); }
  return new Set((data||[]).map(r=>String(r.shared_book_id)));
}
export async function fetchRealFeed(userId,limit=40){
  let visibleIds=[userId];
  if(userId){
    const {data:following,error:followErr}=await supa.from("follows").select("following_id").eq("follower_id",userId);
    if(followErr) console.error("Oncer: caricamento seguiti per il feed fallito —",followErr.message);
    else visibleIds=[userId,...following.map(f=>f.following_id)];
  }
  const {data:pages,error:pagesErr}=await supa
    .from("pages")
    .select("id,user_id,day,year,photo,caption,filter_id,sticker_id,created_at,profiles(username,theme,avatar_url,verified,rewards)")
    .in("user_id",visibleIds)
    .order("created_at",{ascending:false})
    .limit(limit);
  if(pagesErr){ console.error("Oncer: caricamento feed reale fallito —",pagesErr.message); return []; }
  if(!pages||pages.length===0) return [];
  const ids=pages.map(p=>p.id);
  const {data:reactions,error:reactErr}=await supa
    .from("reactions")
    .select("page_id,emoji,user_id")
    .in("page_id",ids);
  if(reactErr) console.error("Oncer: caricamento reazioni fallito —",reactErr.message);
  const reactionsByPage={};
  const myReactionByPage={};
  (reactions||[]).forEach(r=>{
    if(!reactionsByPage[r.page_id]) reactionsByPage[r.page_id]={};
    reactionsByPage[r.page_id][r.emoji]=(reactionsByPage[r.page_id][r.emoji]||0)+1;
    if(userId&&r.user_id===userId) myReactionByPage[r.page_id]=r.emoji;
  });
  return pages.map(p=>({
    id:`real-${p.id}`,
    pageId:p.id,
    ownerId:p.user_id,
    real:true,
    user:(p.profiles&&p.profiles.username)||"oncer",
    ut:(p.profiles&&p.profiles.theme)||"neon",
    avatarUrl:(p.profiles&&p.profiles.avatar_url)||null,
    verified:!!(p.profiles&&p.profiles.verified),
    // Serie di giorni consecutivi con tutti gli obiettivi giornalieri
    // completati — mostrata come badge accanto al nome nel Feed (vedi
    // FeedCard in Feed.jsx). Snapshot al momento del caricamento del feed,
    // non live: se l'autore completa gli obiettivi dopo, si aggiorna al
    // prossimo refresh, non istantaneamente.
    dailyStreak:(p.profiles&&p.profiles.rewards&&p.profiles.rewards.dailyStreak)||null,
    day:p.day,
    date:new Date(p.created_at).toLocaleDateString("it-IT",{day:"numeric",month:"short"}),
    ts:new Date(p.created_at).getTime(),
    photo:p.photo,
    cap:p.caption,
    filter:p.filter_id,
    sticker:p.sticker_id,
    reactions:reactionsByPage[p.id]||{},
    myReaction:myReactionByPage[p.id]||null,
    micro:null,
  }));
}
export async function searchRealUsers(query,excludeId){
  if(!query||!query.trim()) return [];
  let req=supa.from("profiles").select("id,username,theme,avatar_url").ilike("username",`%${query.trim()}%`).limit(20);
  const {data,error}=await req;
  if(error){ console.error("Oncer: ricerca utenti fallita —",error.message); return []; }
  return (data||[]).filter(u=>u.id!==excludeId);
}
export async function fetchRemoteProfile(username){
  const {data,error}=await supa.from("profiles").select("*").eq("username",username).single();
  if(error||!data) return null;
  return data;
}
export async function fetchRemoteBooks(userId){
  // Solo le pagine del vero diario annuale (nessun micro_book_id) — le foto
  // scattate solo per un micro-libro non devono comparire come pagine del
  // diario di quell'utente.
  const {data,error}=await supa.from("pages").select("*").eq("user_id",userId).is("micro_book_id",null).order("day",{ascending:true});
  if(error){ console.error("Oncer: caricamento libri profilo fallito —",error.message); return {}; }
  const booksByYear={};
  (data||[]).forEach(r=>{
    if(!booksByYear[r.year]) booksByYear[r.year]={pages:[]};
    booksByYear[r.year].pages.push({photo:r.photo,caption:r.caption,day:r.day,ts:new Date(r.created_at).getTime(),filter:r.filter_id||"none",sticker:r.sticker_id||null});
  });
  return booksByYear;
}
export async function fetchFollowInfo(myId,theirId){
  const [{count:followers},{count:following},{data:mineToThem},{data:theirsToMe}]=await Promise.all([
    supa.from("follows").select("*",{count:"exact",head:true}).eq("following_id",theirId).eq("status","accepted"),
    supa.from("follows").select("*",{count:"exact",head:true}).eq("follower_id",theirId).eq("status","accepted"),
    myId?supa.from("follows").select("status").eq("follower_id",myId).eq("following_id",theirId).maybeSingle():Promise.resolve({data:null}),
    myId?supa.from("follows").select("status").eq("follower_id",theirId).eq("following_id",myId).maybeSingle():Promise.resolve({data:null}),
  ]);
  // status: null (nessun rapporto) | "pending_sent" (ho chiesto io, in attesa)
  // | "pending_received" (mi hanno chiesto loro, in attesa) | "accepted"
  let status=null;
  if(mineToThem) status=mineToThem.status==="accepted"?"accepted":"pending_sent";
  else if(theirsToMe&&theirsToMe.status==="pending") status="pending_received";
  const libraryVisible=!!((mineToThem&&mineToThem.status==="accepted")||(theirsToMe&&theirsToMe.status==="accepted"));
  return {followers:followers||0,following:following||0,status,libraryVisible};
}
// Manda una richiesta di follow (non segue subito — l'altro deve accettare)
// e avvisa chi la riceve con una notifica.
export async function sendFollowRequest(myId,theirId){
  const {error}=await supa.from("follows").insert({follower_id:myId,following_id:theirId,status:"pending"});
  if(error){ console.error("Oncer: richiesta di follow fallita —",error.message); return false; }
  const {error:notifErr}=await supa.from("notifications").insert({user_id:theirId,type:"follow_request",from_user_id:myId});
  if(notifErr) console.error("Oncer: notifica richiesta follow non creata —",notifErr.message);
  return true;
}
// Annulla una richiesta mandata da me, oppure smette di seguire se già accettato.
export async function cancelOrUnfollow(myId,theirId){
  const {error}=await supa.from("follows").delete().eq("follower_id",myId).eq("following_id",theirId);
  if(error){ console.error("Oncer: annullamento/smetti di seguire fallito —",error.message); return false; }
  return true;
}
// Risponde a una richiesta ricevuta: theirId ha chiesto di seguire myId.
// notifId: id della notifica "follow_request" a cui si sta rispondendo. Va
// cancellata dal database qui, non solo dallo stato locale — altrimenti
// riapparirebbe ogni volta che il pannello notifiche viene ricaricato dal
// server, anche dopo averla già accettata o rifiutata (era il bug segnalato
// da Matte: la richiesta accettata ricompariva ogni volta).
export async function respondToFollowRequest(myId,theirId,accept,notifId){
  if(accept){
    const {error}=await supa.from("follows").update({status:"accepted"}).eq("follower_id",theirId).eq("following_id",myId);
    if(error){ console.error("Oncer: accettazione follow fallita —",error.message); return false; }
    const {error:notifErr}=await supa.from("notifications").insert({user_id:theirId,type:"follow_accepted",from_user_id:myId});
    if(notifErr) console.error("Oncer: notifica accettazione non creata —",notifErr.message);
  } else {
    const {error}=await supa.from("follows").delete().eq("follower_id",theirId).eq("following_id",myId);
    if(error){ console.error("Oncer: rifiuto follow fallito —",error.message); return false; }
  }
  if(notifId){
    const {error:delErr}=await supa.from("notifications").delete().eq("id",notifId);
    if(delErr) console.error("Oncer: rimozione notifica richiesta follow fallita —",delErr.message);
  }
  return true;
}
// Notifica "di sistema" (non da un altro utente, from_user_id nullo) per
// avvisare che un obiettivo è diventato riscattabile — resta nella
// campanella finché non viene letta, oltre al messaggio lampo mostrato
// subito. .select().single() restituisce la riga vera (id, created_at)
// così l'interfaccia può mostrarla subito senza dover ricaricare tutto.
export async function notifyGoalUnlocked(userId,{goalId,rewardId,name,emoji}){
  const {data,error}=await supa.from("notifications").insert({user_id:userId,type:"goal_unlocked",from_user_id:null,data:{goalId,rewardId,name,emoji}}).select().single();
  if(error){ console.error("Oncer: creazione notifica obiettivo sbloccato fallita —",error.message); return null; }
  return data;
}
export async function fetchNotifications(userId){
  const {data,error}=await supa.from("notifications").select("*").eq("user_id",userId).order("created_at",{ascending:false}).limit(50);
  if(error){ console.error("Oncer: caricamento notifiche fallito —",error.message); return []; }
  if(!data||data.length===0) return [];
  const fromIds=[...new Set(data.map(n=>n.from_user_id).filter(Boolean))];
  const {data:profiles}=fromIds.length?await supa.from("profiles").select("id,username,avatar_url,theme").in("id",fromIds):{data:[]};
  const profById={}; (profiles||[]).forEach(p=>{ profById[p.id]=p; });
  return data.map(n=>{
    const p=profById[n.from_user_id];
    return {
      id:n.id,type:n.type,read:n.read,createdAt:n.created_at,data:n.data,
      fromUserId:n.from_user_id,
      fromUsername:p?p.username:"qualcuno",
      fromAvatarUrl:p?p.avatar_url:null,
      fromTheme:p?p.theme:"neon",
    };
  });
}
export async function markNotificationsRead(userId){
  const {error}=await supa.from("notifications").update({read:true}).eq("user_id",userId).eq("read",false);
  if(error) console.error("Oncer: segna-notifiche-lette fallito —",error.message);
}
export async function fetchUnreadNotifCount(userId){
  const {count,error}=await supa.from("notifications").select("*",{count:"exact",head:true}).eq("user_id",userId).eq("read",false);
  if(error){ console.error("Oncer: conteggio notifiche fallito —",error.message); return 0; }
  return count||0;
}
// Le persone che segui davvero (richiesta accettata) — usata per la lista
// "invia pagina a..." nel Feed, così puoi mandarla anche a chi non hai
// ancora una conversazione aperta, non solo a chi ti ha già scritto.
export async function fetchMyFollowing(userId){
  const {data,error}=await supa.from("follows").select("following_id, profiles!follows_following_id_fkey(id,username,theme,avatar_url)").eq("follower_id",userId).eq("status","accepted");
  if(error){
    // Fallback se il nome del vincolo non combacia — due passaggi separati invece di una join.
    const {data:rows,error:err2}=await supa.from("follows").select("following_id").eq("follower_id",userId).eq("status","accepted");
    if(err2){ console.error("Oncer: caricamento following fallito —",err2.message); return []; }
    const ids=(rows||[]).map(r=>r.following_id);
    if(ids.length===0) return [];
    const {data:profiles}=await supa.from("profiles").select("id,username,theme,avatar_url").in("id",ids);
    return (profiles||[]).map(p=>({id:p.id,username:p.username,theme:p.theme,avatarUrl:p.avatar_url}));
  }
  return (data||[]).map(r=>({id:r.profiles.id,username:r.profiles.username,theme:r.profiles.theme,avatarUrl:r.profiles.avatar_url}));
}
// Manda davvero una pagina a una persona reale — trova o crea la
// conversazione, poi inserisce un messaggio con la pagina agganciata.
export async function sharePageToRealThread(myId,theirId,page){
  const thread=await getOrCreateThread(myId,theirId);
  if(!thread) return null;
  const {error}=await supa.from("messages").insert({thread_id:thread.id,sender_id:myId,text:null,page_data:page});
  if(error){ console.error("Oncer: invio pagina condivisa fallito —",error.message); return null; }
  return thread;
}
export async function getOrCreateThread(myId,theirId){
  const {data:existing,error:findErr}=await supa
    .from("threads").select("id,user_a,user_b,hidden_for_user_a,hidden_for_user_b")
    .or(`and(user_a.eq.${myId},user_b.eq.${theirId}),and(user_a.eq.${theirId},user_b.eq.${myId})`)
    .maybeSingle();
  if(findErr) console.error("Oncer: ricerca conversazione fallita —",findErr.message);
  if(existing){
    // Se l'avevo "cancellata" dal mio Notebook (vedi hideThreadForUser) e la sto riaprendo
    // esplicitamente (ricerca o condivisione pagina), torna visibile in lista — altrimenti
    // sparirebbe di nuovo al prossimo caricamento pur avendoci appena scritto dentro.
    const amUserA=existing.user_a===myId;
    const hiddenForMe=amUserA?existing.hidden_for_user_a:existing.hidden_for_user_b;
    if(hiddenForMe){
      await supa.from("threads").update(amUserA?{hidden_for_user_a:false}:{hidden_for_user_b:false}).eq("id",existing.id);
    }
    return existing;
  }
  const {data:created,error:insErr}=await supa.from("threads").insert({user_a:myId,user_b:theirId}).select().single();
  if(insErr){ console.error("Oncer: creazione conversazione fallita —",insErr.message); return null; }
  return created;
}
// Non cancella nulla per davvero: nasconde la conversazione SOLO dalla lista Notebook di chi la
// cancella (comportamento scelto da Matte, come WhatsApp/Instagram). L'altra persona continua a
// vederla normalmente; se scrive di nuovo, il trigger SQL notebook_reveal_thread_on_message la
// fa ricomparire in automatico anche per chi l'aveva nascosta (vedi migrazione
// 20260830183618_notebook_elimina_conversazione.sql).
export async function hideThreadForUser(threadId,myId){
  const {data:thread,error:findErr}=await supa.from("threads").select("id,user_a,user_b").eq("id",threadId).maybeSingle();
  if(findErr||!thread){ console.error("Oncer: cancellazione conversazione fallita — thread non trovato",findErr&&findErr.message); return false; }
  const amUserA=thread.user_a===myId;
  const {error}=await supa.from("threads").update(amUserA?{hidden_for_user_a:true}:{hidden_for_user_b:true}).eq("id",threadId);
  if(error){ console.error("Oncer: cancellazione conversazione fallita —",error.message); return false; }
  return true;
}
export async function fetchThreadMessages(threadId){
  const {data,error}=await supa.from("messages").select("*").eq("thread_id",threadId).order("created_at",{ascending:true});
  if(error){ console.error("Oncer: caricamento messaggi fallito —",error.message); return []; }
  return data||[];
}
export async function fetchMyThreads(myId){
  const {data:allThreads,error}=await supa.from("threads").select("id,user_a,user_b,hidden_for_user_a,hidden_for_user_b").or(`user_a.eq.${myId},user_b.eq.${myId}`);
  if(error){ console.error("Oncer: caricamento conversazioni fallito —",error.message); return []; }
  // Non mostra le conversazioni che l'utente ha cancellato dal proprio Notebook (vedi
  // hideThreadForUser) — restano intatte per l'altra persona, semplicemente non compaiono qui.
  const threads=(allThreads||[]).filter(t=>!(t.user_a===myId?t.hidden_for_user_a:t.hidden_for_user_b));
  if(!threads||threads.length===0) return [];
  const otherIds=threads.map(t=>t.user_a===myId?t.user_b:t.user_a);
  const threadIds=threads.map(t=>t.id);
  const [{data:profiles},{data:allMsgs}]=await Promise.all([
    supa.from("profiles").select("id,username,theme,avatar_url").in("id",otherIds),
    supa.from("messages").select("thread_id,text,sender_id,created_at").in("thread_id",threadIds).order("created_at",{ascending:true}),
  ]);
  const profileById={}; (profiles||[]).forEach(p=>{profileById[p.id]=p;});
  const lastMsgByThread={}; (allMsgs||[]).forEach(m=>{lastMsgByThread[m.thread_id]=m;}); // ascending order → last write wins
  return threads.map(t=>{
    const otherId=t.user_a===myId?t.user_b:t.user_a;
    const prof=profileById[otherId];
    const last=lastMsgByThread[t.id];
    return {
      id:`real-${t.id}`,realThreadId:t.id,real:true,
      user:prof?prof.username:"utente",theirId:otherId,unread:0,avatarUrl:prof?prof.avatar_url:null,
      msgs:last?[shapeMsg(last,myId)]:[],
    };
  });
}
export async function createMicroBook(userId,{title,preset,theme}){
  const {data,error}=await supa.from("micro_books").insert({user_id:userId,title,preset,theme}).select().single();
  if(error){ console.error("Oncer: creazione micro-libro fallita —",error.message); return null; }
  return data;
}
export async function fetchMyMicroBooks(userId){
  const {data,error}=await supa.from("micro_books").select("*, pages(count)").eq("user_id",userId).order("created_at",{ascending:false});
  if(error){ console.error("Oncer: caricamento micro-libri fallito —",error.message); return []; }
  return (data||[]).map(m=>({
    id:m.id,real:true,preset:m.preset,icon:(MP.find(p=>p.id===m.preset)||MP[0]).icon,
    title:m.title,pages:(m.pages&&m.pages[0]&&m.pages[0].count)||0,max:20,closed:m.closed,theme:m.theme,
    cover:(MP.find(p=>p.id===m.preset)||MP[0]).icon,
    dates:new Date(m.created_at).toLocaleDateString("it-IT",{day:"numeric",month:"short",year:"numeric"}),
  }));
}
export async function fetchFollowedMicroPages(userId){
  const {data:following,error:followErr}=await supa.from("follows").select("following_id").eq("follower_id",userId);
  if(followErr){ console.error("Oncer: caricamento seguiti fallito —",followErr.message); return []; }
  if(!following||following.length===0) return [];
  const followedIds=following.map(f=>f.following_id);
  const {data:books,error:booksErr}=await supa.from("micro_books").select("id,user_id,title,preset,theme").in("user_id",followedIds).eq("closed",false);
  if(booksErr){ console.error("Oncer: caricamento micro-libri seguiti fallito —",booksErr.message); return []; }
  if(!books||books.length===0) return [];
  const bookIds=books.map(b=>b.id);
  const [{data:pages,error:pagesErr},{data:profiles}]=await Promise.all([
    supa.from("pages").select("id,photo,caption,micro_book_id,created_at").in("micro_book_id",bookIds).order("created_at",{ascending:false}),
    supa.from("profiles").select("id,username,theme").in("id",followedIds),
  ]);
  if(pagesErr){ console.error("Oncer: caricamento pagine micro-libri seguiti fallito —",pagesErr.message); return []; }
  const bookById={}; books.forEach(b=>{ bookById[b.id]=b; });
  const profileById={}; (profiles||[]).forEach(p=>{ profileById[p.id]=p; });
  return (pages||[]).map(p=>{
    const book=bookById[p.micro_book_id];
    const owner=book?profileById[book.user_id]:null;
    return {
      id:p.id,photo:p.photo,caption:p.caption,
      bookTitle:book?book.title:"",bookPreset:book?book.preset:null,
      username:owner?owner.username:"?",theme:owner?owner.theme:"neon",
    };
  });
}
export async function fetchMicroBookPages(microBookId){
  const {data,error}=await supa.from("pages").select("*").eq("micro_book_id",microBookId).order("created_at",{ascending:true});
  if(error){ console.error("Oncer: caricamento pagine micro-libro fallito —",error.message); return {pages:[],error:error.message}; }
  return {pages:data||[],error:null};
}
export async function closeMicroBook(microBookId){
  const {error}=await supa.from("micro_books").update({closed:true}).eq("id",microBookId);
  if(error) console.error("Oncer: chiusura micro-libro fallita —",error.message);
  return !error;
}
// Cancella un micro-libro PERSONALE (non condiviso) — prima le pagine
// legate ad esso, poi il libro stesso, senza affidarsi a un eventuale
// cascade automatico sul database che potrebbe non esserci.
export async function deleteMicroBook(microBookId,userId){
  const {error:pagesErr}=await supa.from("pages").delete().eq("micro_book_id",microBookId).eq("user_id",userId);
  if(pagesErr){ console.error("Oncer: cancellazione pagine micro-libro fallita —",pagesErr.message); return false; }
  // .select() dopo il delete restituisce le righe DAVVERO cancellate — con
  // RLS attivo ma senza una policy di DELETE, Postgres non dà errore, si
  // limita a cancellare zero righe. Senza questo controllo, quel caso
  // sembrerebbe un successo anche se sul database non è cambiato nulla.
  const {data,error}=await supa.from("micro_books").delete().eq("id",microBookId).eq("user_id",userId).select("id");
  if(error){ console.error("Oncer: cancellazione micro-libro fallita —",error.message); return false; }
  if(!data||data.length===0){ console.error("Oncer: cancellazione micro-libro non ha toccato nessuna riga — probabile policy RLS di DELETE mancante"); return false; }
  return true;
}
// Cancella un libro CONDIVISO (family/micro/evento) — solo chi lo ha creato
// dovrebbe poterlo fare (il controllo vero va comunque garantito da una
// policy RLS lato database, questo è solo il controllo lato client). Prima
// avvisa con una notifica tutti gli altri partecipanti, poi ripulisce le
// pagine e le iscrizioni, infine cancella il libro.
export async function deleteSharedBook(sharedBookId,userId){
  const {data:members,error:memErr}=await supa.from("shared_book_members")
    .select("user_id,shared_books(title,preset)")
    .eq("shared_book_id",sharedBookId);
  if(memErr) console.error("Oncer: lettura membri per notifica cancellazione fallita —",memErr.message);
  const title=(members&&members[0]&&members[0].shared_books&&members[0].shared_books.title)||"";
  const preset=(members&&members[0]&&members[0].shared_books&&members[0].shared_books.preset)||null;
  const others=(members||[]).filter(m=>String(m.user_id)!==String(userId));
  if(others.length>0){
    const {error:notifErr}=await supa.from("notifications").insert(
      others.map(m=>({user_id:m.user_id,type:"shared_book_deleted",from_user_id:userId,data:{sharedBookId,title,preset}}))
    );
    if(notifErr) console.error("Oncer: invio notifiche di cancellazione fallito, si procede comunque —",notifErr.message);
  }
  const {error:pagesErr}=await supa.from("shared_pages").delete().eq("shared_book_id",sharedBookId);
  if(pagesErr) console.error("Oncer: cancellazione pagine libro condiviso fallita —",pagesErr.message);
  const {error:memDelErr}=await supa.from("shared_book_members").delete().eq("shared_book_id",sharedBookId);
  if(memDelErr) console.error("Oncer: cancellazione iscrizioni libro condiviso fallita —",memDelErr.message);
  // .select() dopo il delete restituisce le righe DAVVERO cancellate — vedi
  // nota in deleteMicroBook qui sopra sullo stesso controllo.
  const {data,error}=await supa.from("shared_books").delete().eq("id",sharedBookId).eq("created_by",userId).select("id");
  if(error){ console.error("Oncer: cancellazione libro condiviso fallita —",error.message); return false; }
  if(!data||data.length===0){ console.error("Oncer: cancellazione libro condiviso non ha toccato nessuna riga — probabile policy RLS di DELETE mancante"); return false; }
  return true;
}
export async function fetchUnreadThreadCount(userId){
  const threads=await fetchMyThreads(userId);
  if(threads.length===0) return 0;
  const threadIds=threads.map(th=>th.realThreadId);
  const [{data:allMsgs},{data:reads}]=await Promise.all([
    supa.from("messages").select("thread_id,sender_id,created_at").in("thread_id",threadIds),
    supa.from("thread_reads").select("thread_id,last_read_at").eq("user_id",userId),
  ]);
  const readAt={}; (reads||[]).forEach(r=>{ readAt[r.thread_id]=new Date(r.last_read_at).getTime(); });
  let unreadThreads=0;
  const byThread={};
  (allMsgs||[]).forEach(m=>{ (byThread[m.thread_id]=byThread[m.thread_id]||[]).push(m); });
  Object.keys(byThread).forEach(tid=>{
    const last=readAt[tid]||0;
    const hasUnread=byThread[tid].some(m=>m.sender_id!==userId&&new Date(m.created_at).getTime()>last);
    if(hasUnread) unreadThreads++;
  });
  return unreadThreads;
}
export async function markThreadRead(threadId,userId){
  const {error}=await supa.from("thread_reads").upsert({thread_id:threadId,user_id:userId,last_read_at:new Date().toISOString()});
  if(error) console.error("Oncer: segna-come-letto fallito —",error.message);
}
export async function fetchModerationBlocks(limit=50){
  const {data,error}=await supa.from("moderation_blocks").select("*, profiles(username)").order("created_at",{ascending:false}).limit(limit);
  if(error){ console.error("Oncer: caricamento registro moderazione fallito —",error.message); return []; }
  return (data||[]).map(b=>({
    id:b.id,type:b.type,contentUrl:b.content_url,contentText:b.content_text,reason:b.reason,createdAt:b.created_at,
    username:(b.profiles&&b.profiles.username)||"utente sconosciuto",
  }));
}
export async function getModerationImageSignedUrl(path){
  const {data,error}=await supa.storage.from("moderation-review").createSignedUrl(path,3600);
  if(error){ console.error("Oncer: generazione link foto bloccata fallita —",error.message); return null; }
  return data.signedUrl;
}
export async function createRealFilter(userId,{name,css,tint,vig,grain,vigAmt,grainAmt}){
  const {data,error}=await supa.from("filters").insert({creator_id:userId,name,category:"Community",css,tint,vig,grain,vig_amt:vigAmt??(vig?1:0),grain_amt:grainAmt??(grain?1:0)}).select().single();
  if(error){ console.error("Oncer: pubblicazione filtro fallita —",error.message); return null; }
  return data;
}
export async function fetchCommunityFilters(limit=60){
  const {data,error}=await supa.from("filters").select("*, profiles(username)").order("uses",{ascending:false}).limit(limit);
  if(error){ console.error("Oncer: caricamento filtri community fallito —",error.message); return []; }
  return (data||[]).map(f=>({
    id:f.id,name:f.name,cat:f.category||"Community",
    creator:(f.profiles&&f.profiles.username)||"oncer",
    css:f.css,tint:f.tint,vig:f.vig,grain:f.grain,vigAmt:f.vig_amt,grainAmt:f.grain_amt,
    uses:f.uses||0,official:false,mine:f.creator_id===null,real:true,
  }));
}
export async function fetchActiveEvents(){
  const {data,error}=await supa.from("events").select("*").eq("active",true);
  if(error){ console.error("Oncer: caricamento eventi fallito —",error.message); return []; }
  const m=TODAY.getMonth()+1,d=TODAY.getDate();
  const inWindow=(ev)=>{
    if(ev.start_month===ev.end_month) return m===ev.start_month&&d>=ev.start_day&&d<=ev.end_day;
    // Window wraps across the new year (e.g. Dec 27 → Jan 3)
    if(ev.start_month>ev.end_month) return (m===ev.start_month&&d>=ev.start_day)||(m===ev.end_month&&d<=ev.end_day)||(m>ev.start_month)||(m<ev.end_month);
    return (m===ev.start_month&&d>=ev.start_day)||(m===ev.end_month&&d<=ev.end_day)||(m>ev.start_month&&m<ev.end_month);
  };
  return (data||[]).filter(inWindow);
}
export async function createFutureMessage(userId,{toUserId,recipientType,recipientLabel,content,unlockDate}){
  const {error}=await supa.from("future_messages").insert({
    from_user_id:userId,to_user_id:toUserId||null,recipient_type:recipientType,
    recipient_label:recipientLabel||null,content,unlock_date:unlockDate,
  });
  if(error){ console.error("Oncer: scrittura messaggio futuro fallita —",error.message); return false; }
  return true;
}
export async function fetchMySentFutureMessages(userId){
  const {data,error}=await supa.from("future_messages").select("*").eq("from_user_id",userId).order("unlock_date",{ascending:true});
  if(error){ console.error("Oncer: caricamento messaggi inviati fallito —",error.message); return []; }
  return data||[];
}
export async function fetchDeliveredFutureMessages(userId){
  const today=new Date().toISOString().slice(0,10);
  const {data,error}=await supa.from("future_messages").select("*")
    .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`).lte("unlock_date",today).order("unlock_date",{ascending:false});
  if(error){ console.error("Oncer: caricamento messaggi sbloccati fallito —",error.message); return []; }
  const senderIds=[...new Set((data||[]).map(m=>m.from_user_id))];
  const {data:profiles}=senderIds.length?await supa.from("profiles").select("id,username").in("id",senderIds):{data:[]};
  const nameById={}; (profiles||[]).forEach(p=>{ nameById[p.id]=p.username; });
  return (data||[]).map(m=>({...m,from_username:nameById[m.from_user_id]||"?"}));
}
// ── Funzioni per Scopri (temi/micro-libri/account veri) — rimesse qui in
// modo permanente, dato che erano andate perse in un giro precedente.
export async function fetchThemeRankings(){
  const {data,error}=await supa.from("profiles").select("theme");
  if(error){ console.error("Oncer: classifica temi fallita —",error.message); return []; }
  const counts={};
  (data||[]).forEach(p=>{ if(p.theme) counts[p.theme]=(counts[p.theme]||0)+1; });
  return Object.entries(counts).map(([id,n])=>({id,n})).sort((a,b)=>b.n-a.n).slice(0,6);
}
export async function fetchFeaturedMicroBooks(limit=6){
  const {data,error}=await supa.from("micro_books").select("id,title,preset,closed,profiles(username,theme)").eq("closed",true).order("created_at",{ascending:false}).limit(limit);
  if(error){ console.error("Oncer: micro-libri in evidenza falliti —",error.message); return []; }
  const withCounts=await Promise.all((data||[]).map(async m=>{
    const {count}=await supa.from("pages").select("*",{count:"exact",head:true}).eq("micro_book_id",m.id);
    return {
      u:(m.profiles&&m.profiles.username)||"?",
      theme:(m.profiles&&m.profiles.theme)||"neon",
      book:{icon:"📎",title:m.title,pages:count||0,closed:m.closed,preset:m.preset},
    };
  }));
  return withCounts;
}
export async function fetchDiscoverableUsers(userId,limit=8){
  let q=supa.from("profiles").select("id,username,theme,avatar_url,verified").limit(limit*3);
  if(userId) q=q.neq("id",userId);
  const {data,error}=await q;
  if(error){ console.error("Oncer: account da scoprire falliti —",error.message); return []; }
  const withFollowers=await Promise.all((data||[]).map(async p=>{
    const {count}=await supa.from("follows").select("*",{count:"exact",head:true}).eq("following_id",p.id).eq("status","accepted");
    return {u:p.username,theme:p.theme,photo:p.avatar_url,verified:!!p.verified,f:count||0};
  }));
  return withFollowers.sort((a,b)=>b.f-a.f).slice(0,limit);
}
export async function savePushToken(userId,token){
  const {error}=await supa.from("push_tokens").upsert({user_id:userId,token,platform:"android",updated_at:new Date().toISOString()},{onConflict:"token"});
  if(error) console.error("Oncer: salvataggio push token fallito —",error.message);
}
